import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronRight } from "lucide-react";
import { useContacts } from "@/_actions/contactActions";
import { useMyPropertiesCount } from "@/_actions/propertiesActions";
import { Link } from "react-router";
import usePlanStore from "@/stores/planStore";

function getDaysUntil(dateString) {
  if (!dateString) return 0;

  const targetDate = new Date(`${dateString}T00:00:00`);

  if (Number.isNaN(targetDate.getTime())) {
    return 0;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const days = Math.ceil((targetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  return Math.max(0, days);
}

function getContractRange(startDateString, endDateString) {
  if (!startDateString || !endDateString) return null;

  const startDate = new Date(`${startDateString}T00:00:00`);
  const endDate = new Date(`${endDateString}T23:59:59.999`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (
    Number.isNaN(startDate.getTime()) ||
    Number.isNaN(endDate.getTime()) ||
    startDate > endDate ||
    startDate > today
  ) {
    return null;
  }

  return `${startDateString}T00:00:00,${endDateString}T23:59:59.999999`;
}

export default function PerformanceSummaryCard() {
  const { data: allPropertiesCount = {} } = useMyPropertiesCount();

  const contractStartDate = usePlanStore((state) => state.contractStartDate);
  const contractEndDate = usePlanStore((state) => state.contractEndDate);
  const storedPlan = usePlanStore((state) => state.plan);

  const contractRange = getContractRange(contractStartDate, contractEndDate);
  const {
    data: contacts,
    isLoading: isContactsLoading,
    error: contactsError,
  } = useContacts(
    {
      created_range: contractRange,
      size: 1,
    },
    { enabled: Boolean(contractRange) },
  );

  const daysLeft = getDaysUntil(contractEndDate);
  const opportunitiesCount = contactsError || !contractRange ? "—" : (contacts?.count ?? 0);

  return (
    <Card className="grid grid-cols-3">
      <div className="col-span-3 lg:col-span-1">
        <CardContent className="flex flex-col gap-1">
          <CardTitle className="text-lg font-medium">
            <Link to="/contacts" className="flex items-center gap-1 text-violet-cyt-strong">
              Oportunidades
              <ChevronRight className="text-black" />
            </Link>
          </CardTitle>

          {isContactsLoading ? (
            <Skeleton className="h-10 w-16" aria-label="Cargando oportunidades" />
          ) : (
            <span className="text-4xl font-bold">{opportunitiesCount}</span>
          )}

          <p className="font-medium">Contactos</p>
        </CardContent>
      </div>

      <div className="col-span-3 lg:col-span-1">
        <CardContent className="flex flex-col gap-1">
          <CardTitle className="text-lg font-medium">
            <Link to="/properties" className="flex items-center gap-1 text-violet-cyt-strong">
              Propiedades
              <ChevronRight className="text-black" />
            </Link>
          </CardTitle>

          <span className="text-4xl font-bold">{allPropertiesCount.published_total ?? 0}</span>

          <p className="font-medium">Inventario publicado</p>
        </CardContent>
      </div>

      <div className="col-span-3 lg:col-span-1">
        <CardContent className="flex flex-col gap-1">
          <CardTitle className="text-lg font-medium">
            <Link to="/subscription" className="flex items-center gap-1 text-violet-cyt-strong">
              Días restantes
              <ChevronRight className="text-black" />
            </Link>
          </CardTitle>

          <span className="text-4xl font-bold">{daysLeft}</span>

          <p className="font-medium">Plan {storedPlan?.name}</p>
        </CardContent>
      </div>
    </Card>
  );
}
